import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Scale, BadgeCheck, Phone, Globe, MapPin, Star } from "lucide-react";
import { areaLabel } from "@/lib/attorneyI18n";
import { pageText } from "@/lib/i18n/pages";

const FILTERS = [
  { key: "all", en: "All", es: "Todos", areas: null },
  { key: "family", en: "Divorce & custody", es: "Divorcio y custodia", areas: ["Divorce & Custody", "Family Law"] },
  { key: "eviction", en: "Eviction", es: "Desalojo", areas: ["Landlord/Tenant"] },
  { key: "criminal", en: "Criminal defense", es: "Defensa penal", areas: ["Criminal Defense", "Traffic"] },
];

const L = {
  en: { title: (p) => `Local lawyers serving ${p}`, sub: (n, c) => `${n} attorneys in our directory list ${c} County.`, all: "Open full directory →", verified: "Claimed", cert: "Board certified", more: (n) => `Show all ${n}`, none: "No lawyers in our directory list this county yet.", noneFilter: "No lawyers match this filter yet.", featured: "Featured", feature: "Are you a lawyer here? Get featured →" },
  es: { title: (p) => `Abogados locales que atienden ${p}`, sub: (n, c) => `${n} abogados de nuestro directorio atienden el condado de ${c}.`, all: "Ver directorio completo →", verified: "Verificado", cert: "Especialista certificado", more: (n) => `Ver los ${n}`, none: "Aún no hay abogados de este condado en nuestro directorio.", noneFilter: "Ningún abogado coincide con este filtro.", featured: "Destacado", feature: "¿Es abogado aquí? Destaque su perfil →" },
};

const rank = (a, city) => [
  a.is_resource ? 1 : 0,
  city && (a.city || "").toLowerCase() === city.toLowerCase() ? 0 : 1,
  a.claim_status === "approved" ? 0 : 1,
  a.board_certified ? 0 : 1,
  (a.name || "").toLowerCase(),
];
const cmp = (x, y) => { for (let i = 0; i < x.length; i++) { if (x[i] < y[i]) return -1; if (x[i] > y[i]) return 1; } return 0; };
const dayRotate = (arr) => { if (arr.length < 2) return arr; const k = Math.floor(Date.now() / 86400000) % arr.length; return [...arr.slice(k), ...arr.slice(0, k)]; };

const Row = ({ a, t, featured, onTrack, lang }) => (
  <div className={`p-4 flex flex-wrap items-start justify-between gap-3 ${featured ? "bg-accent/5" : ""}`} data-testid={`${featured ? "featured" : "local"}-lawyer-${a.id}`}>
    <div className="min-w-0">
      <p className="font-medium text-foreground flex flex-wrap items-center gap-2">
        {featured && <span className="inline-flex items-center gap-1 rounded-full bg-accent text-accent-foreground px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide"><Star size={11} /> {t.featured}</span>}
        {a.name}
        {a.claim_status === "approved" && <span className="inline-flex items-center gap-1 text-[0.7rem] font-semibold text-emerald-600 dark:text-emerald-400"><BadgeCheck size={13} /> {t.verified}</span>}
        {a.board_certified && <span className="text-[0.7rem] font-semibold text-accent">{t.cert}</span>}
      </p>
      <p className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-3">
        {[a.firm, a.city && <span key="c" className="inline-flex items-center gap-1"><MapPin size={11} />{a.city}</span>, (a.practice_areas || []).slice(0, 3).map((p) => areaLabel(lang, p)).join(" · ")].filter(Boolean).map((x, i) => <span key={i}>{x}</span>)}
      </p>
    </div>
    <div className="flex items-center gap-3 text-sm">
      {a.phone && <a href={`tel:${a.phone}`} onClick={() => featured && onTrack(a.id, "call")} data-testid={`lawyer-call-${a.id}`} className="inline-flex items-center gap-1 text-accent hover:underline"><Phone size={13} />{a.phone}</a>}
      {a.website && <a href={a.website} target="_blank" rel="noreferrer" onClick={() => featured && onTrack(a.id, "web")} className="inline-flex items-center gap-1 text-accent hover:underline"><Globe size={13} /> Web</a>}
    </div>
  </div>
);

export const LocalLawyers = ({ county, city, place, lang = "en" }) => {
  const t = pageText(lang, "local") || L[lang] || L.en;
  const [list, setList] = useState(null);
  const [featuredIds, setFeaturedIds] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    api.get("/directory/attorneys", { params: { county } }).then(({ data }) => setList(data.attorneys || [])).catch(() => setList([]));
    api.get("/featured", { params: { county } }).then(({ data }) => setFeaturedIds(data.attorney_ids || [])).catch(() => {});
  }, [county]);

  const areas = FILTERS.find((f) => f.key === filter)?.areas;
  const matches = (a) => !areas || (a.practice_areas || []).some((p) => areas.includes(p));
  const featured = useMemo(() => dayRotate((list || []).filter((a) => featuredIds.includes(a.id))), [list, featuredIds]);
  const sorted = useMemo(() => (list || []).filter((a) => !featuredIds.includes(a.id)).sort((a, b) => cmp(rank(a, city), rank(b, city))), [list, city, featuredIds]);
  const viewedKey = featured.map((a) => a.id).join(",");
  useEffect(() => {
    if (viewedKey) api.post("/featured/track", { county, attorney_ids: viewedKey.split(","), type: "view" }).catch(() => {});
  }, [viewedKey, county]);
  const track = (id, type) => api.post("/featured/track", { county, attorney_ids: [id], type }).catch(() => {});
  if (!list) return null;
  const shown = sorted.filter((a) => (areas ? !a.is_resource && matches(a) : true));
  const shownFeatured = featured.filter(matches);
  const lawyers = list.filter((a) => !a.is_resource).length;

  return (
    <section className="max-w-5xl mx-auto px-6 py-8" data-testid="local-lawyers">
      <h2 className="font-serif text-3xl tracking-tight mb-2 flex items-center gap-2"><Scale className="text-accent" /> {t.title(place)}</h2>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <p className="text-muted-foreground" data-testid="local-lawyers-count">{lawyers ? t.sub(lawyers, county) : t.none}</p>
        <Link to={`/find-attorney?county=${encodeURIComponent(county)}`} className="text-sm text-accent font-semibold hover:underline" data-testid="local-lawyers-directory-link">{t.all}</Link>
      </div>
      {lawyers > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="local-lawyers-filters">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => { setFilter(f.key); setShowAll(false); }} data-testid={`lawyer-filter-${f.key}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${filter === f.key ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {f[lang] || pageText(lang, "filters")?.[f.key] || f.en}
            </button>
          ))}
        </div>
      )}
      {(shown.length > 0 || shownFeatured.length > 0) ? (
        <div className="grid-card divide-y divide-border max-h-[560px] overflow-y-auto" data-testid="local-lawyers-list">
          {shownFeatured.map((a) => <Row key={`f-${a.id}`} a={a} t={t} featured onTrack={track} lang={lang} />)}
          {shown.slice(0, showAll ? shown.length : 25).map((a) => <Row key={a.id} a={a} t={t} lang={lang} />)}
          {!showAll && shown.length > 25 && (
            <button type="button" onClick={() => setShowAll(true)} data-testid="local-lawyers-show-all"
              className="w-full p-3 text-sm font-semibold text-accent hover:bg-secondary transition-colors">{t.more(shown.length)}</button>
          )}
        </div>
      ) : lawyers > 0 && <p className="text-sm text-muted-foreground" data-testid="local-lawyers-empty">{t.noneFilter}</p>}
      <Link to="/featured" className="inline-block mt-3 text-xs text-muted-foreground hover:text-accent" data-testid="local-lawyers-get-featured">{t.feature}</Link>
    </section>
  );
};
